import React from 'react';
import { useNavigate } from 'react-router-dom';
import PlatformLayout from './PlatformLayout';

import PlatformText from './PlatformText';

const TermsOfService = () => {
  const navigate = useNavigate();


  const handleBack = () => {
    navigate(-1);
  };

  return (
    <PlatformLayout>
      <div className="terms-of-service-container">
        <div className="terms-of-service-header">
          <button 
            onClick={handleBack}
            className="back-button"
          >
            ← Back
          </button>
          <PlatformText variant="h1" className="terms-of-service-title">
            Terms of Service
          </PlatformText>
          <PlatformText variant="body" className="terms-of-service-subtitle">
            Ordease - Order Management System
          </PlatformText>
        </div>

        <div className="important-notice">
          <PlatformText variant="body" className="important-text">
            <strong>IMPORTANT:</strong> By accessing or using the Ordease application and services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our service.
          </PlatformText>
        </div>

        <div className="terms-of-service-content">
          <section className="terms-section">
            <PlatformText variant="h2">1. Acceptance of Terms</PlatformText>
            <PlatformText variant="body">
              These Terms of Service ("Terms") govern your use of the Ordease order management application and related services (collectively, the "Service") provided by Ordease ("we," "our," or "us").
            </PlatformText>
            <PlatformText variant="body">
              By accessing or using our Service, you agree to be bound by these Terms and our Privacy Policy. If you are using the Service on behalf of an organization, you represent that you have the authority to bind that organization to these Terms.
            </PlatformText>
            <PlatformText variant="body">
              <strong>Terms Acceptance Requirement:</strong> By creating an account, you must explicitly accept these Terms of Service and our Privacy Policy. This acceptance is required during registration and is recorded with a timestamp. You cannot use our Service without accepting these Terms.
            </PlatformText>
          </section>

          <section className="terms-section">
            <PlatformText variant="h2">2. Description of Service</PlatformText>
            <PlatformText variant="body">Ordease is a cloud-based order management system designed for businesses to:</PlatformText>
            <ul className="terms-list">
              <li>Create and manage customer orders</li>
              <li>Track order status and progress</li>
              <li>Manage customer information</li>
              <li>Generate reports and analytics</li>
              <li>Collaborate with team members</li>
              <li>Process documents and images</li>
            </ul>
            <PlatformText variant="body">We reserve the right to modify, suspend, or discontinue any part of the Service at any time with reasonable notice.</PlatformText>
          </section>

          <section className="terms-section">
            <PlatformText variant="h2">3. User Accounts and Registration</PlatformText>
            
            <PlatformText variant="h3">3.1 Account Creation</PlatformText>
            <PlatformText variant="body">To use our Service, you must:</PlatformText>
            <ul className="terms-list">
              <li>Be at least 18 years old or have parental consent</li>
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized use</li>
            </ul>

            <PlatformText variant="h3">3.2 Organization Registration</PlatformText>
            <PlatformText variant="body">Organizations must:</PlatformText>
            <ul className="terms-list">
              <li>Provide valid business information</li>
              <li>Designate authorized administrators</li>
              <li>Ensure all users comply with these Terms</li>
              <li>Maintain appropriate security measures</li>
            </ul>

            <PlatformText variant="h3">3.3 Role-Based Access</PlatformText>
            <PlatformText variant="body">Our Service supports multiple user roles with different permissions:</PlatformText>
            <ul className="terms-list">
              <li><strong>Business Owner:</strong> Full access to all features, user management, organization settings, subscription management, and analytics</li>
              <li><strong>Salesperson:</strong> Order creation, editing, customer management, and uploading order-related documents</li>
              <li><strong>Accountant:</strong> Order approval, billing approval, financial reporting, and access to bill/challan documents</li>
              <li><strong>Inventory Manager:</strong> Stock management, inventory operations, and access to billing documents (when enabled by organization settings)</li>
            </ul>
            <PlatformText variant="body">
              <strong>Note:</strong> Business Owners control role assignments and access permissions. Only Business Owners can add users, assign roles, and manage subscription plans.
            </PlatformText>

            <PlatformText variant="h3">3.4 Account Responsibilities</PlatformText>
            <PlatformText variant="body">You are responsible for:</PlatformText>
            <ul className="terms-list">
              <li>All activities under your account</li>
              <li>Maintaining the confidentiality of your credentials</li>
              <li>Ensuring your account information is accurate</li>
              <li>Notifying us of any security concerns</li>
              <li>Complying with your assigned role permissions</li>
              <li>Managing your subscription and billing preferences</li>
            </ul>
          </section>

          <section className="terms-section">
            <PlatformText variant="h2">4. Acceptable Use Policy</PlatformText>
            
            <PlatformText variant="h3">4.1 Permitted Uses</PlatformText>
            <PlatformText variant="body">You may use our Service for lawful business purposes related to order management and customer relationship management.</PlatformText>

            <PlatformText variant="h3">4.2 Prohibited Uses</PlatformText>
            <PlatformText variant="body">You agree not to:</PlatformText>
            <ul className="terms-list">
              <li>Use the Service for any illegal or unauthorized purpose</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on intellectual property rights</li>
              <li>Transmit harmful, offensive, or inappropriate content</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Use automated systems to access the Service</li>
              <li>Share account credentials with unauthorized users</li>
              <li>Reverse engineer or attempt to extract source code</li>
            </ul>
          </section>

          <section className="terms-section">
            <PlatformText variant="h2">5. Data and Content</PlatformText>
            
            <PlatformText variant="h3">5.1 Your Data</PlatformText>
            <PlatformText variant="body">You retain ownership of all data you upload or create using our Service. You grant us a limited license to:</PlatformText>
            <ul className="terms-list">
              <li>Store and process your data to provide the Service</li>
              <li>Use aggregated, anonymized data for service improvement</li>
              <li>Backup and secure your data</li>
            </ul>

            <PlatformText variant="h3">5.2 Data Accuracy</PlatformText>
            <PlatformText variant="body">You are responsible for:</PlatformText>
            <ul className="terms-list">
              <li>The accuracy and completeness of your data</li>
              <li>Ensuring you have rights to use any uploaded content</li>
              <li>Complying with data protection laws</li>
              <li>Obtaining necessary consents for data processing</li>
            </ul>

            <PlatformText variant="h3">5.3 Data Retention</PlatformText>
            <PlatformText variant="body">We will retain your data as described in our Privacy Policy. You may export your data at any time, subject to reasonable limitations.</PlatformText>
            
            <PlatformText variant="h3">5.4 Data Retention Periods and File Deletion</PlatformText>
            <PlatformText variant="body">Files, documents, and data uploaded to your orders are automatically retained based on your subscription plan's data retention period:</PlatformText>
            <ul className="terms-list">
              <li><strong>Basic Plan:</strong> 30 days - Files and order data may be automatically deleted after 30 days</li>
              <li><strong>Premium Plan:</strong> 65 days - Files and order data may be automatically deleted after 65 days</li>
              <li><strong>Premium+ Plan:</strong> 90 days - Files and order data may be automatically deleted after 90 days</li>
              <li><strong>Enterprise Plan:</strong> Forever - No automatic deletion of files or order data</li>
            </ul>
            <PlatformText variant="body">
              <strong>Important:</strong> This includes all files uploaded to orders, including bills, challans, bilty documents, product images, and order details. After the retention period expires, these files may be permanently deleted and cannot be recovered. We strongly recommend downloading important business documents before they reach the retention limit or upgrading to a plan with longer retention.
            </PlatformText>
            <PlatformText variant="body">
              Account information, user data, and subscription records are retained separately and are not subject to these retention periods.
            </PlatformText>
          </section>

          <section className="terms-section">
            <PlatformText variant="h2">6. Service Availability and Support</PlatformText>
            
            <PlatformText variant="h3">6.1 Service Availability</PlatformText>
            <PlatformText variant="body">We strive to maintain high service availability but do not guarantee uninterrupted access. We may perform maintenance with reasonable notice.</PlatformText>

            <PlatformText variant="h3">6.2 Support</PlatformText>
            <PlatformText variant="body">We provide support through:</PlatformText>
            <ul className="terms-list">
              <li>In-app help and documentation</li>
              <li>Email support for technical issues</li>
              <li>Regular updates and improvements</li>
            </ul>

            <PlatformText variant="h3">6.3 Updates</PlatformText>
            <PlatformText variant="body">We may update the Service to improve functionality, security, or compliance. Updates may be automatic or require user action.</PlatformText>
          </section>

          <section className="terms-section">
            <PlatformText variant="h2">7. Privacy and Data Protection</PlatformText>
            <PlatformText variant="body">Your privacy is important to us. Our collection, use, and protection of your personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference.</PlatformText>
            <PlatformText variant="body">You agree to comply with applicable data protection laws and regulations in your use of the Service.</PlatformText>
          </section>

          <section className="terms-section">
            <PlatformText variant="h2">8. Intellectual Property</PlatformText>
            
            <PlatformText variant="h3">8.1 Our Rights</PlatformText>
            <PlatformText variant="body">We retain all rights to:</PlatformText>
            <ul className="terms-list">
              <li>The Ordease application and platform</li>
              <li>Our trademarks, logos, and branding</li>
              <li>Proprietary technology and algorithms</li>
              <li>Documentation and materials</li>
            </ul>

            <PlatformText variant="h3">8.2 Your Rights</PlatformText>
            <PlatformText variant="body">You retain ownership of your data and content. You grant us a limited license to use your content solely to provide the Service.</PlatformText>

            <PlatformText variant="h3">8.3 Feedback</PlatformText>
            <PlatformText variant="body">If you provide feedback or suggestions, you grant us a non-exclusive, perpetual license to use such feedback to improve our Service.</PlatformText>
          </section>

          <section className="terms-section">
            <PlatformText variant="h2">9. Free Trial and Subscription Services</PlatformText>
            
            <PlatformText variant="h3">9.1 Free Trial</PlatformText>
            <PlatformText variant="body">We offer a 30-day free trial for new organizations. During the trial period:</PlatformText>
            <ul className="terms-list">
              <li>You have full access to all features of the service</li>
              <li>No payment information is required to start the trial</li>
              <li>You may cancel at any time during the trial period</li>
              <li>At the end of the 30-day trial period, your access will be restricted until you subscribe to a paid plan</li>
              <li>If you do not subscribe, you will only be able to view existing orders; new order creation and modifications will be disabled</li>
              <li>We may send trial-related notifications and reminders at 7 days, 3 days, and 1 day before expiration</li>
              <li>You can upgrade to a paid plan at any time during or after the trial period</li>
            </ul>

            <PlatformText variant="h3">9.2 Subscription Plans</PlatformText>
            <PlatformText variant="body">We offer multiple subscription plans with different features and limits:</PlatformText>
            <ul className="terms-list">
              <li><strong>Basic Plan:</strong> ₹999/month or ₹9,999/year - Up to 6 users, 80 orders, 30-day backup</li>
              <li><strong>Premium Plan:</strong> ₹1,499/month or ₹14,999/year - Up to 12 users, 200 orders, 65-day backup</li>
              <li><strong>Premium+ Plan:</strong> ₹2,199/month or ₹21,999/year - Up to 20 users, 400 orders, 90-day backup</li>
              <li><strong>Enterprise Plan:</strong> ₹3,199/month or ₹35,199/year - Unlimited users and orders, forever backup</li>
            </ul>

            <PlatformText variant="h3">9.3 Billing and Payment</PlatformText>
            <PlatformText variant="body">Subscription billing terms:</PlatformText>
            <ul className="terms-list">
              <li>Monthly and yearly billing options available</li>
              <li>Yearly plans offer up to 17% savings (approximately 2 months free)</li>
              <li>Additional users beyond plan limits charged at ₹50 per user per month</li>
              <li>Payment is due in advance for each billing period</li>
              <li>Late payments may result in service suspension</li>
              <li>All prices are in Indian Rupees (INR) unless otherwise specified</li>
              <li>GST and applicable taxes will be added to all subscriptions as per Indian tax laws</li>
              <li>We accept payment via UPI, Credit/Debit Cards, and Net Banking through Razorpay payment gateway</li>
              <li>Subscriptions automatically renew unless cancelled before the renewal date</li>
            </ul>

            <PlatformText variant="h3">9.4 Usage Limits and Restrictions</PlatformText>
            <PlatformText variant="body">Each subscription plan includes specific limits on usage that apply to the billing period:</PlatformText>
            <ul className="terms-list">
              <li><strong>Basic Plan:</strong> Up to 6 users and 80 orders per period</li>
              <li><strong>Premium Plan:</strong> Up to 12 users and 200 orders per period</li>
              <li><strong>Premium+ Plan:</strong> Up to 20 users and 400 orders per period</li>
              <li><strong>Enterprise Plan:</strong> Unlimited users and unlimited orders</li>
            </ul>
            <PlatformText variant="body">When you approach or exceed your plan limits:</PlatformText>
            <ul className="terms-list">
              <li>You will receive notifications encouraging you to upgrade</li>
              <li>You may be unable to create new orders or add users until limits are increased</li>
              <li>Additional users beyond plan limits are automatically charged at ₹50 per user per month</li>
              <li>Orders are tracked cumulatively - the limit applies to total orders created during your billing period</li>
              <li>Limits reset at the start of each new billing period</li>
            </ul>

            <PlatformText variant="h3">9.5 Plan Changes and Cancellation</PlatformText>
            <PlatformText variant="body">You may:</PlatformText>
            <ul className="terms-list">
              <li>Upgrade your plan at any time with immediate effect (prorated charges apply)</li>
              <li>Downgrade your plan at the end of your current billing period</li>
              <li>Cancel your subscription with 30 days notice</li>
              <li>Export your data before cancellation</li>
            </ul>

            <PlatformText variant="h3">9.6 Refunds</PlatformText>
            <PlatformText variant="body">Refund policies are determined by your service agreement and applicable laws. Generally, we do not provide refunds for partial months or unused portions of your subscription. Refund requests for subscription upgrades or changes are handled on a case-by-case basis. Contact support for refund inquiries.</PlatformText>
          </section>

          <section className="terms-section">
            <PlatformText variant="h2">10. Platform-Specific Terms</PlatformText>
            
            <PlatformText variant="h3">10.1 Mobile Applications</PlatformText>
            <PlatformText variant="body">Our Service is available through native mobile applications for iOS and Android:</PlatformText>
            <ul className="terms-list">
              <li>iOS app is available through the Apple App Store</li>
              <li>Android app is available through Google Play Store</li>
              <li>Mobile apps may have platform-specific features and limitations</li>
              <li>App updates may be required for continued functionality</li>
              <li>Platform providers may have their own terms and conditions</li>
            </ul>

            <PlatformText variant="h3">10.2 Progressive Web App (PWA)</PlatformText>
            <PlatformText variant="body">Our Service is also available as a Progressive Web App:</PlatformText>
            <ul className="terms-list">
              <li>Can be installed on mobile devices and desktop computers</li>
              <li>Works offline with limited functionality</li>
              <li>May require internet connection for full features</li>
              <li>Browser compatibility may vary</li>
            </ul>

            <PlatformText variant="h3">10.3 Multi-Language Support</PlatformText>
            <PlatformText variant="body">Our Service supports multiple languages:</PlatformText>
            <ul className="terms-list">
              <li>English (default)</li>
              <li>Hindi (हिन्दी)</li>
              <li>Language preferences are saved per user</li>
              <li>Some features may not be fully translated</li>
            </ul>

            <PlatformText variant="h3">10.4 Subscription Management</PlatformText>
            <PlatformText variant="body">Subscriptions for the Ordease service are administered through the organization’s designated billing portal. The iOS app does not provide subscription purchase or modification functionality.</PlatformText>
            <ul className="terms-list">
              <li>Native mobile applications (iOS and Android) display your current subscription information but do not support in-app purchases or payment processing</li>
              <li>Subscription purchases, upgrades, modifications, and cancellations are completed through the organization’s billing portal or designated web experience</li>
              <li>Mobile apps provide read-only access to your subscription status and plan details</li>
              <li>All payment processing and subscription changes occur outside of the iOS application for security and compliance purposes</li>
              <li>Payments are processed through Razorpay payment gateway (web-only)</li>
            </ul>
          </section>

          <section className="terms-section">
            <PlatformText variant="h2">11. Limitation of Liability</PlatformText>
            
            <PlatformText variant="h3">11.1 Disclaimer of Warranties</PlatformText>
            <PlatformText variant="body">THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.</PlatformText>

            <PlatformText variant="h3">11.2 Limitation of Liability</PlatformText>
            <PlatformText variant="body">IN NO EVENT SHALL WE BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR USE, ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICE.</PlatformText>

            <PlatformText variant="h3">11.3 Maximum Liability</PlatformText>
            <PlatformText variant="body">OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT PAID BY YOU FOR THE SERVICE IN THE TWELVE MONTHS PRECEDING THE CLAIM.</PlatformText>
          </section>

          <section className="terms-section">
            <PlatformText variant="h2">12. Indemnification</PlatformText>
            <PlatformText variant="body">You agree to indemnify and hold harmless Ordease and its officers, directors, employees, and agents from and against any claims, damages, losses, and expenses arising out of or relating to:</PlatformText>
            <ul className="terms-list">
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Your violation of applicable laws</li>
            </ul>
          </section>

          <section className="terms-section">
            <PlatformText variant="h2">13. Termination</PlatformText>
            
            <PlatformText variant="h3">13.1 Termination by You</PlatformText>
            <PlatformText variant="body">You may terminate your account at any time by contacting us or using the account deletion feature. For subscription accounts, termination will take effect at the end of your current billing period.</PlatformText>

            <PlatformText variant="h3">13.2 Termination by Us</PlatformText>
            <PlatformText variant="body">We may terminate or suspend your access to the Service:</PlatformText>
            <ul className="terms-list">
              <li>For violation of these Terms</li>
              <li>For non-payment of subscription fees</li>
              <li>For extended periods of inactivity</li>
              <li>At the end of your free trial period without subscription</li>
              <li>As required by law</li>
            </ul>

            <PlatformText variant="h3">13.3 Effect of Termination</PlatformText>
            <PlatformText variant="body">Upon termination:</PlatformText>
            <ul className="terms-list">
              <li>Your access to the Service will cease</li>
              <li>We will retain your data as described in our Privacy Policy</li>
              <li>You may export your data within 30 days</li>
              <li>Provisions that survive termination will remain in effect</li>
              <li>Subscription fees are non-refundable</li>
            </ul>
          </section>

          <section className="terms-section">
            <PlatformText variant="h2">14. Governing Law and Dispute Resolution</PlatformText>
            
            <PlatformText variant="h3">14.1 Governing Law</PlatformText>
            <PlatformText variant="body">These Terms are governed by the laws of India, without regard to conflict of law principles. Any disputes shall be subject to the exclusive jurisdiction of courts in New Delhi, India.</PlatformText>

            <PlatformText variant="h3">14.2 Dispute Resolution</PlatformText>
            <PlatformText variant="body">Any disputes arising from these Terms or the Service shall be resolved through:</PlatformText>
            <ol className="terms-list">
              <li>Good faith negotiations between the parties</li>
              <li>Mediation if negotiations fail</li>
              <li>Binding arbitration in accordance with applicable rules</li>
            </ol>

            <PlatformText variant="h3">14.3 Class Action Waiver</PlatformText>
            <PlatformText variant="body">You agree to resolve disputes individually and waive any right to participate in class action lawsuits.</PlatformText>
          </section>

          <section className="terms-section">
            <PlatformText variant="h2">15. Indian Legal Compliance</PlatformText>
            
            <PlatformText variant="h3">15.1 Applicable Laws</PlatformText>
            <PlatformText variant="body">This service complies with Indian laws and regulations including:</PlatformText>
            <ul className="terms-list">
              <li>Digital Personal Data Protection Act, 2023</li>
              <li>Information Technology Act, 2000</li>
              <li>Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011</li>
              <li>Consumer Protection Act, 2019</li>
              <li>Indian Contract Act, 1872</li>
            </ul>

            <PlatformText variant="h3">15.2 Data Protection Compliance</PlatformText>
            <PlatformText variant="body">We process personal data in accordance with the Digital Personal Data Protection Act, 2023:</PlatformText>
            <ul className="terms-list">
              <li><strong>Lawful Basis:</strong> Data processing is based on legitimate business interests and contractual necessity</li>
              <li><strong>Data Minimization:</strong> We collect only necessary data for service provision</li>
              <li><strong>Purpose Limitation:</strong> Data is used only for stated purposes</li>
              <li><strong>Storage Limitation:</strong> Data is retained only as long as necessary</li>
              <li><strong>Accuracy:</strong> We maintain accurate and up-to-date data</li>
            </ul>

            <PlatformText variant="h3">15.3 Indian Payment Terms</PlatformText>
            <PlatformText variant="body">For Indian users:</PlatformText>
            <ul className="terms-list">
              <li>All prices are in Indian Rupees (INR)</li>
              <li>GST applicable as per Indian tax laws</li>
              <li>UPI and RuPay payments accepted where available</li>
              <li>Refunds processed within 5-7 business days</li>
              <li>Payment disputes resolved through Indian banking channels</li>
            </ul>

            <PlatformText variant="h3">15.4 Data Residency</PlatformText>
            <PlatformText variant="body">Your data is primarily stored in Google Cloud data centers in India, ensuring compliance with data localization requirements under Indian law.</PlatformText>

            <PlatformText variant="h3">15.5 Grievance Redressal</PlatformText>
            <PlatformText variant="body">For complaints and grievances:</PlatformText>
            <ul className="terms-list">
              <li><strong>Primary Contact:</strong> help.ordease@gmail.com</li>
              <li><strong>Response Time:</strong> Within 48 hours</li>
              <li><strong>Data Protection Officer:</strong> help.ordease@gmail.com</li>
              <li><strong>Escalation:</strong> Available through our support channels</li>
            </ul>
          </section>

          <section className="terms-section">
            <PlatformText variant="h2">16. Miscellaneous</PlatformText>
            
            <PlatformText variant="h3">16.1 Entire Agreement</PlatformText>
            <PlatformText variant="body">These Terms, together with our Privacy Policy, constitute the entire agreement between you and us regarding the Service.</PlatformText>

            <PlatformText variant="h3">16.2 Severability</PlatformText>
            <PlatformText variant="body">If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full force and effect.</PlatformText>

            <PlatformText variant="h3">16.3 Waiver</PlatformText>
            <PlatformText variant="body">Our failure to enforce any provision of these Terms does not constitute a waiver of that provision.</PlatformText>

            <PlatformText variant="h3">16.4 Assignment</PlatformText>
            <PlatformText variant="body">You may not assign these Terms without our written consent. We may assign these Terms to any successor or affiliate.</PlatformText>

            <PlatformText variant="h3">16.5 Force Majeure</PlatformText>
            <PlatformText variant="body">We are not liable for any failure to perform due to circumstances beyond our reasonable control.</PlatformText>

            <PlatformText variant="h3">16.6 Governing Law</PlatformText>
            <PlatformText variant="body">These Terms are governed by the laws of India, without regard to conflict of law principles. Any disputes shall be subject to the exclusive jurisdiction of courts in New Delhi, India.</PlatformText>
          </section>

          <section className="terms-section">
            <PlatformText variant="h2">17. Contact Information</PlatformText>
            <div className="contact-info">
              <PlatformText variant="body">If you have any questions about these Terms of Service, please contact us:</PlatformText>
              <ul className="terms-list">
                <li><strong>Email:</strong> help.ordease@gmail.com</li>
                <li><strong>Address:</strong> New Delhi, India</li>
                <li><strong>Phone:</strong> +91 9971162351</li>
                <li><strong>Website:</strong> {process.env.REACT_APP_WEBSITE_URL || 'https://www.ordease.in'}</li>
              </ul>
            </div>
          </section>

          <div className="terms-footer">
            <PlatformText variant="body" className="last-updated">
              <strong>Last Updated:</strong> January 2025
            </PlatformText>
            <PlatformText variant="body" className="version">
              <strong>Version:</strong> 3.0
            </PlatformText>
          </div>
        </div>
      </div>
    </PlatformLayout>
  );
};

export default TermsOfService; 